import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	deleteDocument,
	editDocumentName,
	uploadDocument,
} from 'feature/upload/uploadSlice';
import { IFraudTrackingDetail, IFraudTrackingState } from 'types/FraudTracking';
import {
	downloadFraudTrackingThunk,
	getFraudTrackingDetailThunk,
	getFraudTrackingThunk,
	saveFraudThunk,
} from './fraudTrackingThunk';

const initialFraudDetail: IFraudTrackingDetail = {
	fraudId: 0,
	claimUUID: '',
	detectionDate: '',
	investigationStatus: '',
	outcomeSummary: '',
	referralDate: '',
	internalNotes: '',
	createdBy: {
		memberFirstName: '',
		memberLastName: '',
		memberRole: '',
		memberRoleLabel: '',
	},
	attachments: [],
};

const initialState: IFraudTrackingState = {
	rows: [],
	fraudDetail: {
		fraudId: 0,
		claimUUID: '',
		detectionDate: '',
		investigationStatus: '',
		outcomeSummary: '',
		referralDate: '',
		internalNotes: '',
		createdBy: {
			memberFirstName: '',
			memberLastName: '',
			memberRole: '',
			memberRoleLabel: '',
		},
		attachments: [],
	},
	triggerRefresh: false,
	pages: 1,
	page: 1,
	search: false,
};

export const getFraudTracking = createAsyncThunk(
	'fraudTracking/getFraudTracking',
	getFraudTrackingThunk,
);

export const getFraudTrackingDetail = createAsyncThunk(
	'fraudTracking/getFraudTrackingDetail',
	getFraudTrackingDetailThunk,
);

export const downloadFraudTracking = createAsyncThunk(
	'fraudTracking/downloadFraudTracking',
	downloadFraudTrackingThunk,
);

export const saveFraud = createAsyncThunk(
	'fraudTracking/saveFraud',
	saveFraudThunk,
);

const fraudTrackingSlice = createSlice({
	name: 'fraudTracking',
	initialState,
	reducers: {
		setFraudPage: (state, { payload }: PayloadAction<number>) => {
			state.page = payload;
		},
		resetFraudDetail: (state, { payload }: PayloadAction<string>) => {
			state.fraudDetail = {
				...initialFraudDetail,
				claimUUID: payload,
			};
		},
		setFraudValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				name: keyof IFraudTrackingDetail;
				value: IFraudTrackingDetail[keyof IFraudTrackingDetail];
			}>,
		) => {
			(state.fraudDetail[
				payload.name
			] as IFraudTrackingDetail[keyof IFraudTrackingDetail]) = payload.value;
		},
		clearFraudAttachment: (state, { payload }) => ({
			...state,
			claimData: {
				...state.fraudDetail,
				attachments: state.fraudDetail!.attachments.filter(
					attachment => attachment.uuid !== payload.uuid,
				),
			},
		}),
	},
	extraReducers: builder => {
		builder
			.addCase(getFraudTracking.fulfilled, (state, { payload }) => {
				state.rows = payload.frauds;
				state.pages = payload.pages;
			})
			.addCase(getFraudTracking.rejected, state => {
				state.rows = [];
			})
			.addCase(uploadDocument.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { docType },
					},
				} = action;

				const { uuid, fileName } = action.payload;

				if (!state.fraudDetail) return;

				if (docType === 'fraud') {
					state.fraudDetail.attachments = [
						...state.fraudDetail.attachments,
						{ uuid, fileName },
					];
				}
			})
			.addCase(deleteDocument.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { uuid },
					},
				} = action;

				if (!state.fraudDetail) return;

				state.fraudDetail.attachments = state.fraudDetail.attachments?.filter(
					attachment => attachment.uuid !== uuid,
				);
			})
			.addCase(editDocumentName.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { documentUuid },
					},
				} = action;

				if (!state.fraudDetail) return;

				return {
					...state,
					fraudDetail: {
						...state.fraudDetail,
						attachments: [
							...state.fraudDetail.attachments.map(el =>
								el.uuid !== documentUuid
									? el
									: {
											uuid: documentUuid,
											fileName: action.payload.fileName,
										},
							),
						],
					},
				};
			})
			.addCase(getFraudTrackingDetail.fulfilled, (state, { payload }) => {
				if (!payload) {
					state.fraudDetail = { ...initialFraudDetail };
					return;
				}

				state.fraudDetail = payload;
				state.triggerRefresh = false;
			})
			.addCase(getFraudTrackingDetail.rejected, state => {
				state.fraudDetail = { ...initialFraudDetail };
			})
			.addCase(saveFraud.fulfilled, (state, { payload }) => {
				state.fraudDetail = { ...state.fraudDetail, fraudId: payload.fraudId };
				state.triggerRefresh = true;
			});
	},
});

export const {
	setFraudPage,
	setFraudValue,
	resetFraudDetail,
	clearFraudAttachment,
} = fraudTrackingSlice.actions;
export default fraudTrackingSlice.reducer;
